import React from "react";
import { Trophy, Star, Navigation } from "lucide-react";
import { useSiteContent } from "../data/SiteContentContext";

export default function Awards() {
  const { content } = useSiteContent();
  const a = content?.awards || {};
  const eyebrow = a.eyebrow || "Awards & Recognition";
  const headlinePre = a.headline_pre || "Voted";
  const headlineHl = a.headline_hl || "Best Tacos";
  const headlinePost = a.headline_post || "in Queens";
  const subtext = a.subtext || "Award-winning taste. Built fresh every day.";
  const body = a.body || "BTW has been recognized for delivering bold flavor and quality you can taste. From tacos to wings, everything we serve is made to stand out.";
  const accent = a.accent || "Real food. Real recognition. No shortcuts.";
  const image = a.image || "/awards/best-tacos.jpg";

  return (
    <section
      id="awards"
      data-testid="awards-section"
      className="relative bg-[#0B0B0B] text-white py-12 sm:py-24 overflow-hidden"
    >
      {/* Subtle radial accents */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 18% 30%, rgba(30,107,255,0.10), transparent 60%), radial-gradient(700px 400px at 85% 70%, rgba(163,217,0,0.06), transparent 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-y-8 gap-x-10 lg:gap-x-14 items-start lg:items-center">
        {/* TITLE BLOCK (mobile: 1st / desktop: top right) */}
        <div className="order-1 lg:order-2 lg:col-start-8 lg:col-span-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#A3D900]/30 bg-[#A3D900]/10 px-4 py-1.5">
              <Trophy size={14} className="text-[#A3D900]" />
              <span className="text-[10px] sm:text-xs font-extrabold tracking-[0.3em] uppercase text-[#A3D900]">
                {eyebrow}
              </span>
            </div>
          </div>

          <h2 className="mt-4 font-display text-5xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
            {headlinePre}{" "}
            <span style={{ color: "#5FBDD6" }}>{headlineHl}</span>
            <br />
            {headlinePost}
          </h2>
        </div>

        {/* PHOTO BLOCK (mobile: 2nd / desktop: full left, spans both rows) */}
        <div
          className="order-2 lg:order-1 lg:col-start-1 lg:col-span-7 lg:row-span-2 lg:self-start relative"
          data-testid="awards-photo-wrap"
        >
          <div
            aria-hidden="true"
            className="absolute -inset-2 sm:-inset-3 rounded-[28px] blur-2xl opacity-40"
            style={{
              background:
                "linear-gradient(135deg, rgba(95,189,214,0.45), rgba(163,217,0,0.35))",
            }}
          />
          <figure className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
            <img
              src={image}
              alt="BTW team receiving the Best Tacos in Queens award plaque"
              loading="lazy"
              className="block w-full h-auto object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/55 via-transparent to-transparent pointer-events-none" />

            <figcaption className="absolute left-4 bottom-4 sm:left-6 sm:bottom-6 inline-flex items-center gap-2 rounded-full bg-[#0B0B0B]/85 backdrop-blur-sm border border-white/10 px-4 py-2">
              <Star size={14} className="text-[#A3D900]" />
              <span className="text-[11px] sm:text-xs font-extrabold tracking-[0.18em] uppercase">
                Award Ceremony · Queens, NY
              </span>
            </figcaption>
          </figure>

          <BestTacosStamp />
        </div>

        {/* BODY BLOCK (mobile: 3rd / desktop: bottom right) */}
        <div className="order-3 lg:order-3 lg:col-start-8 lg:col-span-5">
          <p className="text-white/85 text-lg sm:text-xl font-semibold leading-snug max-w-md">
            {subtext}
          </p>
          <p className="mt-4 text-white/65 text-sm sm:text-base leading-relaxed max-w-lg">
            {body}
          </p>

          <p className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold tracking-[0.18em] uppercase">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "#A3D900" }}
            />
            <span className="text-white/90">
              {accent}
            </span>
          </p>

          <div
            data-testid="awards-social-proof"
            className="mt-8 flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 max-w-md"
          >
            <div className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  size={16}
                  className="fill-[#A3D900] stroke-[#A3D900]"
                />
              ))}
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold text-white">
                Loved by the community
              </div>
              <div className="text-xs text-white/55">
                Backed by real reviews
              </div>
            </div>
          </div>

          {/* Google Reviews + Directions chips */}
          <div className="mt-3 flex flex-wrap gap-2 max-w-md" data-testid="awards-google-row">
            <a
              href="https://www.google.com/maps/search/?api=1&query=BTW+Burgers+Tacos+Wings+Seafood+110-46+Merrick+Blvd+Jamaica+NY+11433"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] hover:border-[#A3D900]/40 px-3 py-2 transition-colors"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white">
                {/* Google "G" icon */}
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path fill="#4285F4" d="M21.6 12.227c0-.696-.062-1.367-.18-2H12v3.78h5.39a4.61 4.61 0 0 1-2 3.026v2.512h3.235c1.892-1.745 2.985-4.317 2.985-7.318z"/>
                  <path fill="#34A853" d="M12 22c2.7 0 4.964-.895 6.625-2.455l-3.234-2.51c-.896.6-2.041.957-3.391.957-2.605 0-4.81-1.76-5.598-4.123H3.061v2.59A9.997 9.997 0 0 0 12 22z"/>
                  <path fill="#FBBC04" d="M6.402 13.869a5.999 5.999 0 0 1 0-3.738V7.541H3.061a9.998 9.998 0 0 0 0 8.918l3.341-2.59z"/>
                  <path fill="#EA4335" d="M12 5.96c1.469 0 2.788.505 3.825 1.497l2.869-2.869C16.96 2.99 14.696 2 12 2A9.997 9.997 0 0 0 3.061 7.541l3.341 2.59C7.19 7.72 9.395 5.96 12 5.96z"/>
                </svg>
              </span>
              <span className="text-xs font-extrabold tracking-[0.18em] uppercase text-white/90 group-hover:text-white">
                Google Reviews
              </span>
            </a>

            <a
              href="https://www.google.com/maps/dir/?api=1&destination=110-46+Merrick+Blvd+Jamaica+NY+11433"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-directions-link"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] hover:border-[#5FBDD6]/40 px-3 py-2 transition-colors"
            >
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-full"
                style={{ background: "#5FBDD6", color: "#0B0B0B" }}
              >
                <Navigation size={12} className="-ml-px -mt-px" fill="currentColor" />
              </span>
              <span className="text-xs font-extrabold tracking-[0.18em] uppercase text-white/90 group-hover:text-white">
                Get Directions
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BestTacosStamp() {
  return (
    <div
      data-testid="best-tacos-stamp"
      className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 lg:-bottom-8 lg:-right-8 h-24 w-24 sm:h-32 sm:w-32 lg:h-40 lg:w-40 flex items-center justify-center"
      aria-hidden="true"
    >
      {/* Outer ring */}
      <svg
        viewBox="0 0 200 200"
        className="absolute inset-0 h-full w-full animate-[spin_20s_linear_infinite]"
      >
        <defs>
          <path
            id="awardCircle"
            d="M 100,100 m -88,0 a 88,88 0 1,1 176,0 a 88,88 0 1,1 -176,0"
          />
        </defs>
        <text
          fill="#FFFFFF"
          fontSize="12"
          fontWeight="900"
          letterSpacing="3"
          fontFamily="Anton, Impact, sans-serif"
        >
          <textPath href="#awardCircle" startOffset="0">
            ★ BEST TACOS ★ QUEENS, NY ★ 2024 ★ BTW ★
          </textPath>
        </text>
      </svg>

      {/* Inner solid */}
      <div
        className="relative h-14 w-14 sm:h-20 sm:w-20 lg:h-24 lg:w-24 rounded-full flex flex-col items-center justify-center text-center shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
        style={{
          background: "#A3D900",
          color: "#0B0B0B",
          border: "3px solid #0B0B0B",
        }}
      >
        <Trophy size={16} className="mb-0.5 sm:hidden" />
        <Trophy size={20} className="mb-0.5 hidden sm:block" />
        <span className="font-display text-[8px] sm:text-[10px] uppercase leading-none tracking-tight">
          Best
        </span>
        <span className="font-display text-xs sm:text-base uppercase leading-none tracking-tight">
          Tacos
        </span>
        <span className="text-[6px] sm:text-[8px] font-extrabold tracking-[0.18em] mt-0.5">
          QUEENS NY
        </span>
      </div>
    </div>
  );
}
